import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

import { dumiProjects, languageIcons } from "../data";

import { motion } from "framer-motion";
import { fadeIn } from "../variants";


const ProjectScreen = () => {
    const { id } = useParams();
    const [project] = useState(
        dumiProjects.find((project) => project.id === parseInt(id))
    );
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const galleryImages = project && project.images ? project.images.slice(1) : [];

    React.useEffect(() => {
        if (!isGalleryOpen) return;

        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % galleryImages.length);
        }, 4000);

        return () => clearInterval(interval);
    }, [isGalleryOpen, galleryImages.length]);

    console.log(project);

    const getLanguageIcon = (lang) => languageIcons[lang] || null;

    return (
        <div className="bg-site bg-contain min-h-screen pt-2 lg:pt-4 pb-8">
            <section id="project-detail" className="section">
                <div className="w-full flex justify-center px-2 lg:px-4">
                    <motion.div
                        variants={fadeIn("up", 0.3)}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: false, amount: 0.3 }}
                        className="bg-white rounded-3xl shadow-2xl overflow-visible w-full max-w-7xl relative"
                    >
                        {/* BACK BUTTON */}
                        <Link
                            to="/"
                            className="absolute -top-6 sm:-top-8 lg:-top-10 left-4 lg:left-8 cursor-pointer z-40"
                        >
                            <div className="flex items-center gap-2 bg-mainBlue text-white rounded-full px-4 py-2 hover:bg-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl">
                                <FaArrowLeft className="text-lg" />
                                <span className="font-semibold hidden sm:inline">Volver</span>
                            </div>
                        </Link>

                        {/* CARD CONTENT */}
                        <div className="p-6 lg:p-10">
                            {/* TITLE */}
                            <h2 className="text-mainYellow text-3xl lg:text-5xl font-bold text-center mb-10">
                                {project.name}
                            </h2>

                            {/* MAIN IMAGE/VIDEO */}
                            <div className="flex justify-center mb-10">
                                <div className="w-full max-w-2xl rounded-2xl overflow-hidden">
                                    {project.images[0].type === "image" ? (
                                        <img
                                            src={project.images[0].url}
                                            alt={project.name}
                                            className="w-full h-auto object-cover"
                                        />
                                    ) : project.images[0].url.endsWith('.gif') ? (
                                        <img
                                            src={project.images[0].url}
                                            alt={project.name}
                                            className="w-full h-auto object-cover"
                                        />
                                    ) : (
                                        <video
                                            src={project.images[0].url}
                                            className="w-full h-auto"
                                            controls
                                            autoPlay
                                            muted
                                        />
                                    )}
                                </div>
                            </div>

                            {/* DESCRIPTION */}
                            <p className="text-mainBlue text-center text-base lg:text-lg leading-relaxed mb-10">
                                {project.description}
                            </p>

                            {/* INFO SECTION */}
                            <div className="border-y border-gray-300 py-8 mb-10">
                                <div className="space-y-4">
                                    {/* PLATFORMS */}
                                    {project.platform && project.platform.length > 0 && (
                                        <div className="flex items-center gap-3">
                                            <span className="text-mainYellow text-2xl">✓</span>
                                            <div>
                                                <span className="font-bold text-mainBlue">Plataformas: </span>
                                                <span className="text-mainBlue ml-2">
                                                    {project.platform.join(", ")}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* LANGUAGES */}
                                    {project.language && project.language.length > 0 && (
                                        <div className="flex items-center gap-3">
                                            <span className="text-mainYellow text-2xl">✓</span>
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-mainBlue">Lenguajes: </span>
                                                <div className="flex gap-3">
                                                    {project.language.map((lang, idx) => {
                                                        const icon = getLanguageIcon(lang);
                                                        return icon ? (
                                                            <img
                                                                key={idx}
                                                                src={icon}
                                                                alt={lang}
                                                                className="h-6 w-6 object-contain"
                                                                title={lang}
                                                            />
                                                        ) : null;
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ACTION BUTTONS */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={() => {
                                        setIsGalleryOpen(true);
                                    }}
                                    className="flex-1 bg-mainBlue text-white py-3 rounded-lg font-bold text-lg hover:bg-blue-800 transition-all duration-300"
                                >
                                    Ver galería
                                </button>
                                {project.url && (
                                    <a
                                        href={project.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1"
                                    >
                                        <button className="w-full bg-mainYellow text-white py-3 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all duration-300">
                                            Ver proyecto
                                        </button>
                                    </a>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* GALLERY MODAL - SLIDESHOW */}
                {isGalleryOpen && galleryImages.length > 0 && (
                    <div
                        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setIsGalleryOpen(false);
                            }
                        }}
                    >
                        <div className="bg-white rounded-3xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-6 lg:p-8 border-b-2 border-gray-200 bg-white">
                                <h3 className="text-3xl lg:text-4xl font-bold text-mainBlue">
                                    Galería
                                </h3>
                                <button
                                    onClick={() => setIsGalleryOpen(false)}
                                    className="text-4xl text-mainBlue hover:text-mainYellow transition-colors duration-300 hover:scale-110"
                                >
                                    ×
                                </button>
                            </div>

                            {/* Slideshow Content */}
                            <div className="px-8 lg:px-12 py-12 lg:py-16 bg-gray-50 flex flex-col items-center">
                                <div className="w-full max-w-2xl flex justify-center">
                                    <div className="w-full h-80 lg:h-96 flex items-center justify-center bg-gray-100 rounded-3xl overflow-hidden">
                                        <img
                                            key={currentImageIndex}
                                            src={galleryImages[currentImageIndex].url}
                                            alt={`${project.name} ${currentImageIndex}`}
                                            className="w-full h-full object-contain transition-opacity duration-500"
                                        />
                                    </div>
                                </div>

                                {/* Image Counter and Dots */}
                                <div className="mt-8 flex flex-col items-center gap-4">
                                    <div className="text-mainBlue font-semibold">
                                        {currentImageIndex + 1} / {galleryImages.length}
                                    </div>
                                    <div className="flex gap-2">
                                        {galleryImages.map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setCurrentImageIndex(index)}
                                                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                                    index === currentImageIndex
                                                        ? "bg-mainBlue w-8"
                                                        : "bg-gray-300 hover:bg-gray-400"
                                                }`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default ProjectScreen;